import u from '@/utils'
import Promise from 'bluebird'
import axios from 'axios'
import moment from 'moment/moment'

const state = {
  profiles: [],
  profileHistory: [],
  profiledBots: {},
  profiledHoneyForms: {},
  profiledWorkflows: {},
  profiledRules: {},
  profiledHoneyUsers: {},
  profiledSelfServices: {},
  totalProfiles: 0
}

const mutations = {
  setProfiles(state, profiles) {
    state.profiles = profiles
  },

  setProfileHistory(state, profileHistory) {
    state.profileHistory = profileHistory
  },
  setTotalProfiles(state, totalCount) {
    state.totalProfiles = totalCount
  },
  setProfiledBots(state, payload) {
    state.profiledBots = {
      ...state.profiledBots,
      [payload.profile.hexId]: payload.bots
    }
  },
  setProfiledHoneyForms(state, payload) {
    state.profiledHoneyForms = {
      ...state.profiledHoneyForms,
      [payload.profile.hexId]: payload.honeyForms
    }
  },

  setProfiledWorkflows(state, payload) {
    state.profiledWorkflows = {
      ...state.profiledWorkflows,
      [payload.profile.hexId]: payload.workflows
    }
  },

  setProfiledRules(state, payload) {
    state.profiledRules = {
      ...state.profiledRules,
      [payload.profile.hexId]: payload.rules
    }
  },

  setProfiledHoneyUsers(state, payload) {
    state.profiledHoneyUsers = {
      ...state.profiledHoneyUsers,
      [payload.profile.hexId]: payload.honeyUsers
    }
  },

  setProfiledSelfServices(state, payload) {
    state.profiledSelfServices = {
      ...state.profiledSelfServices,
      [payload.profile.hexId]: payload.selfServices
    }
  },

  updateProfile(state, profile) {
    let index = state.profiles.findIndex(item => item.hexId === profile.hexId)
    state.profiles.splice(index, 1, profile)
  }
}

const actions = {
  async fetchProfiles({ commit }) {
    commit('setProfiles', [])
    let response = await axios.get(
      '/api/v1/profiles?projection=withParentProfile'
    )
    let profiles = [...response.data._embedded.profiles]
    profiles = profiles.map(profile => {
      let temp = u.processResp(profile)

      if (temp.lastReplicated) {
        temp.lastReplicatedLocal = moment(temp.lastReplicated)
          .utc()
          .local()
          .format('DD MMM YYYY hh:mm:ss a')
      }
      return temp
    })

    profiles = profiles.sort(function(a, b) {
      return new Date(a.createdAtLocal) - new Date(b.createdAtLocal)
    })
    let nodes = {}

    let profilesTree = profiles.filter(profile => {
      let id = profile['hexId']
      let parentId = profile['parentProfileHexId']

      nodes[id] = Object.assign(profile, nodes[id], { children: [] })
      parentId &&
        (nodes[parentId] = nodes[parentId] || { children: [] })[
          'children'
        ].push(profile)

      return !parentId
    })
    commit('setProfiles', profilesTree)
  },

  async replicateCandidateGroup({ commit }, profileId) {
    let response = await axios.post(`/api/v1/profiles/${profileId}/replicate`)
    return response
  },

  async fetchProfiledBots({ commit }, profile) {
    let response = await axios.get(`/api/v1/profiles/${profile.hexId}/bots`)
    let bots = [...response.data._embedded.bots]
    commit('setProfiledBots', { profile, bots })
  },

  async fetchProfiledHoneyForms({ commit }, profile) {
    let response = await axios.get(
      `/api/v1/profiles/${profile.hexId}/honeyForms`
    )
    let honeyForms = [...response.data._embedded.honeyForms]
    commit('setProfiledHoneyForms', { profile, honeyForms })
  },

  async fetchProfiledWorkflows({ commit }, profile) {
    let response = await axios.get(
      `/api/v1/profiles/${profile.hexId}/workflows`
    )
    let workflows = [...response.data._embedded.workflows]
    commit('setProfiledWorkflows', { profile, workflows })
  },

  async fetchProfiledRules({ commit }, profile) {
    let response = await axios.get(`/api/v1/profiles/${profile.hexId}/rules`)
    let rules = [...response.data._embedded.rules]
    commit('setProfiledRules', { profile, rules })
  },

  async deleteProfile(context, hexId) {
    await axios.delete(`/api/v1/profiles/${hexId}`)
  },

  async fetchProfiledHoneyUsers({ commit }, profile) {
    let response = await axios.get(
      `/api/v1/profiles/${profile.hexId}/honeyUsers`
    )
    let honeyUsers = [...response.data._embedded.honeyUsers]
    commit('setProfiledHoneyUsers', { profile, honeyUsers })
  },

  async fetchProfiledSelfServices({ commit }, profile) {
    let response = await axios.get(
      `/api/v1/profiles/${profile.hexId}/selfServices`
    )
    let selfServices = [...response.data._embedded.selfServices]
    commit('setProfiledSelfServices', { profile, selfServices })
  },

  async updateProfile({ commit }, { body, hexId }) {
    let response = await axios.patch(`/api/v1/profiles/${hexId}`, body)
    response.data = u.processResp(response.data)
    commit('updateProfile', response.data)
    return response.data
  },

  async saveProfile(context, payload) {
    let profile = {
      name: payload.name,
      description: payload.description
    }
    if (payload.parentProfile) {
      profile.parentProfile = `/api/v1/profiles/${payload.parentProfile.hexId}`
    }
    let response = await axios.post('/api/v1/profiles', profile)
    return u.processResp(response.data)
  },

  addHoneyFormToProfile(context, payload) {
    return Promise.all(
      payload.honeyForms.map(async form => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/honeyForms/${form.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  deleteHoneyFormFromProfile(context, payload) {
    return Promise.all(
      payload.honeyForms.map(form => {
        return axios.delete(
          `/api/v1/honeyForms/${form.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  },
  addBotToProfile(context, payload) {
    return Promise.all(
      payload.bots.map(async bot => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/bots/${bot.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  deleteBotFromProfile(context, payload) {
    return Promise.all(
      payload.bots.map(bot => {
        return axios.delete(
          `/api/v1/bots/${bot.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  },

  addWorkflowToProfile(context, payload) {
    return Promise.all(
      payload.workflows.map(async workflow => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/workflows/${workflow.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  deleteWorkflowFromProfile(context, payload) {
    return Promise.all(
      payload.workflows.map(workflow => {
        return axios.delete(
          `/api/v1/workflows/${workflow.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  },

  addRuleToProfile(context, payload) {
    return Promise.all(
      payload.rules.map(async rule => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/rules/${rule.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  deleteRuleFromProfile(context, payload) {
    return Promise.all(
      payload.rules.map(rule => {
        return axios.delete(
          `/api/v1/rules/${rule.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  },

  addHoneyUserToProfile(context, payload) {
    return Promise.all(
      payload.honeyUsers.map(async honeyUser => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/honeyUsers/${honeyUser.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  deleteHoneyUserFromProfile(context, payload) {
    return Promise.all(
      payload.honeyUsers.map(honeyUser => {
        return axios.delete(
          `/api/v1/honeyUsers/${honeyUser.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  },

  addSelfServiceToProfile(context, payload) {
    return Promise.all(
      payload.selfServices.map(async selfService => {
        let data = [`/api/v1/profiles/${payload.profile.hexId}`].join('\n')
        let response = await axios.post(
          `/api/v1/selfServices/${selfService.hexId}/profiles`,
          data,
          {
            headers: {
              'Content-Type': 'text/uri-list'
            }
          }
        )
        return response.data
      })
    )
  },

  async fetchProfileHistory({ commit }, urlParams) {
    let url = '/api/core/audit/revisions/profile?'
    if (urlParams !== undefined && urlParams !== null) {
      url = url + '&' + urlParams
    }
    let response = await axios.get(url)
    if (response.data) {
      let profileHistory = [...response.data.content]
      commit('setProfileHistory', profileHistory)
      commit('setTotalProfiles', response.data.totalElements)
      return profileHistory
    }
  },

  deleteSelfServiceFromProfile(context, payload) {
    return Promise.all(
      payload.selfServices.map(selfService => {
        return axios.delete(
          `/api/v1/selfServices/${selfService.hexId}/profiles/${payload.profile.hexId}`
        )
      })
    )
  }
}

export default {
  state,
  actions,
  mutations
}
